import { ReactNode, useEffect, useMemo, useState } from 'react';

import {
  ChakraFlex,
  ChakraText,
  Email,
  EnvelopeUnread,
  EquipUIFireflyV2Theme,
  EquipUITheme,
} from '@equip.health/ui';

import { NotificationStatus } from '~/lib/constants/notifications.constants';
import {
  getNotificationIdsToUpdate,
  getTimestamp,
} from '~/lib/util/notification.util';

import NotificationTooltip from './NotificationTooltip';

const TOP_LEVEL_ID = 'notification-timestamp';

/** 60 seconds */
const REFRESH_NOTIFICATION_TIMESTAMP_INTERVAL = 60000;

interface NotificationTimeStampProps {
  createdAt?: string;
  isRead?: boolean;
  notificationExternalId?: string;
  onUpdateNotification?: (
    notificationExternalIds: Array<string>,
    status: NotificationStatus,
  ) => void;
  relatedNotifications?: RelatedNotificationType[];
}

const { caption } = EquipUITheme.typography;
const { primary, neutral } = EquipUIFireflyV2Theme.colors;

const NotificationTimeStamp = ({
  createdAt,
  isRead,
  notificationExternalId,
  onUpdateNotification,
  relatedNotifications,
}: NotificationTimeStampProps) => {
  const [timestamp, setTimestamp] = useState<string>(getTimestamp(createdAt));

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimestamp(getTimestamp(createdAt));
    }, REFRESH_NOTIFICATION_TIMESTAMP_INTERVAL);
    return () => clearInterval(intervalId);
  }, [createdAt]);

  const onMarkHandler = (
    event: React.MouseEvent<HTMLElement>,
    status: NotificationStatus,
  ): void => {
    event.stopPropagation();
    onUpdateNotification(
      [
        notificationExternalId,
        ...getNotificationIdsToUpdate(status, relatedNotifications),
      ],
      status,
    );
  };

  const tooltipChildren = useMemo<ReactNode>(() => {
    return isRead ? (
      <Email fill={primary[100]} height="14px" width="14px" />
    ) : (
      <EnvelopeUnread fill={neutral.secondary} height="15px" width="15px" />
    );
  }, [isRead]);

  return (
    <ChakraFlex alignItems="center" id={`${TOP_LEVEL_ID}-wrapper`}>
      <ChakraText
        {...caption}
        id={`${TOP_LEVEL_ID}`}
        minWidth="78px"
        paddingBottom="2px"
        paddingRight="4px"
        textAlign="right"
      >
        {timestamp}
      </ChakraText>
      {!isRead && (
        <NotificationTooltip isRead={isRead} onMarkHandler={onMarkHandler}>
          {tooltipChildren}
        </NotificationTooltip>
      )}
    </ChakraFlex>
  );
};

export default NotificationTimeStamp;
